import { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Button, Heading, HStack, Image, rem, Text, VStack } from '@bonusly/design-system';
import { useCurrentUser } from '@bonusly/who-am-i';

import appStore from '../../../../../assets/images/app-store.png';
import bonuslyLogo from '../../../../../assets/images/bonusly-logo.png';
import googlePlay from '../../../../../assets/images/google-play.png';
import { InviteFormData } from './invite-form';
import { formatReceiverNames, getFirstName } from './utils';

export interface InvitePreviewProps {
	formData: InviteFormData;
}

export const InvitePreview: FC<InvitePreviewProps> = ({ formData }) => {
	const { t } = useTranslation(['recognition', 'admin']);
	const currentUser = useCurrentUser();
	return (
		<Box maxW="600px" mx="auto">
			<VStack align="stretch" spacing={rem(16)} py={rem(20)}>
				<VStack align="start" spacing={rem(30)} bgColor="bg.surface" p={rem(20)} borderRadius="md" py={rem(40)}>
					<Image alignSelf="center" src={bonuslyLogo} alt="bonusly logo" h={rem(60)} />

					<Heading size="2xl">{formData.title}</Heading>

					{formData.inviteList.length > 0 && (
						<Text>
							{t('invite_others_modal.greeting', {
								name: getFirstName(formData.inviteList[0].displayName),
							})}
						</Text>
					)}

					<Text>{formData.body}</Text>

					<Text fontSize="sm">
						{t('invite_others_modal.sent_by', {
							name: currentUser?.display_name || '[Your Name]',
							company: 'Bonusly',
						})}
					</Text>
					<Box textAlign="center" py={rem(8)} alignSelf="center">
						<Button colorScheme="primary" size="md" px={rem(32)} py={rem(12)} borderRadius="md" fontWeight="semibold">
							{t('invite_others_modal.join_conversation')}
						</Button>
					</Box>

					{formData.inviteList.length > 1 && (
						<Text fontSize="sm">
							{formData.inviteList.length === 2
								? t('invite_others_modal.others_invited_one', {
										name: getFirstName(formData.inviteList[1].displayName),
									})
								: t('invite_others_modal.others_invited_other', {
										names: formatReceiverNames(
											formData.inviteList.slice(1).map((user) => getFirstName(user.displayName)),
											t
										),
									})}
						</Text>
					)}
				</VStack>
				<VStack spacing={0} pt={rem(40)} bgColor="bg.subtle">
					<Text fontSize={rem(12)} fontWeight="normal" color="text.softest" mb={2.5}>
						{t('admin:company.customization.email_customization.download_app')}
					</Text>
					<HStack>
						<Image src={appStore} alt="app store" cursor="default" />
						<Image src={googlePlay} alt="google play" cursor="default" />
					</HStack>
					<Text fontSize={rem(12)} fontWeight="medium" color="text.softest" display="inline" mt={6}>
						<Trans i18nKey="manage_or_unsubscribe" ns="admin">
							<Text as="span" fontSize={1} fontWeight="medium" color="text.interactive" display="inline" cursor="default" mr={1}>
								Manage email preferences
							</Text>
							or
							<Text as="span" fontSize={1} fontWeight="medium" color="text.interactive" display="inline" cursor="default" ml={1}>
								Unsubscribe
							</Text>
						</Trans>
					</Text>
					<Text fontSize={rem(10)} fontWeight="normal" color="text.softest" mt={2}>
						{t('admin:company.customization.email_customization.copyright', { year: new Date().getFullYear() })}
					</Text>
				</VStack>
			</VStack>
		</Box>
	);
};
